import React, { useCallback, useEffect, useState, useContext } from 'react';
import { CountryCode, CountryName, CountryNameKey } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import CountrySwitcher from '@components/shared/CountrySwitcher';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { MEDIA } from '@constants';
import { getCountryCodeByLocale } from '@utils/language';
import useChosenCountry from '@utils/useChosenCountry';
import useCurrentURLLanguage from '@utils/useCurrentUrlLanguage';
import { useGeolocationCountryCode } from '@utils/useGeolocationCountryCode';
import Modal from '../shared/Modal';

const RegionSwitcherModal = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(null);

  const [defaultCountry, setDefaultCountry] = useState<CountryCode | null>(null);
  const { updateChosenCountry } = useChosenCountry();
  const { state } = useContext(Context);
  const { chosenCountry } = state;
  const currentLanguage = useCurrentURLLanguage();
  const { geolocationCountryCode } = useGeolocationCountryCode(!chosenCountry);

  // Determine country code and name from current locale
  const currentCountryCode = getCountryCodeByLocale(currentLanguage.locale);
  const currentCountryName = CountryName[currentCountryCode as CountryNameKey] || '';

  useEffect(() => {
    const currentCountryCodeByURL = getCountryCodeByLocale(currentLanguage.locale);
    const shouldOpen = !chosenCountry && !!geolocationCountryCode && currentCountryCodeByURL !== geolocationCountryCode;

    setIsOpen(shouldOpen);
  }, [geolocationCountryCode, chosenCountry, currentLanguage]);

  const handleChangeCountry = useCallback(() => {
    const countryToChange = selectedCountry || defaultCountry;

    if (countryToChange) {
      updateChosenCountry(countryToChange);
    }
    setIsOpen(false);
  }, [selectedCountry, defaultCountry, updateChosenCountry]);

  const handleStayInCountry = useCallback(() => {
    updateChosenCountry(currentCountryCode);
    setIsOpen(false);
  }, [currentCountryCode, updateChosenCountry]);

  return (
    <Modal modalOpen={isOpen} title={t('components.change-country')} contentOverflow={'auto'} paddingDesktop="30px">
      <CountrySwitcherTitle $variant={TextVariant.Heading5}>{t('components.change-country-modal-title')}</CountrySwitcherTitle>
      <CountrySwitcher
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        defaultCountry={geolocationCountryCode}
        setDefaultCountry={setDefaultCountry}
      />
      <CountryControlsWrapper>
        <Button onClick={handleStayInCountry} backgroundColor={Colors.paleGold50} buttonTextColor={Colors.forestGreen}>
          {t('components.stay-in-country-button', { countryName: currentCountryName })}
        </Button>
        <Button onClick={handleChangeCountry}>
          {selectedCountry ? t('components.change-your-country-button') : t('components.this-is-my-correct-country-button')}
        </Button>
      </CountryControlsWrapper>
    </Modal>
  );
};

export default RegionSwitcherModal;

const CountryControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 20px;

  button {
    border-radius: 2px;
    flex-grow: 1;
    width: 100%;
  }

  ${MEDIA.mobileL} {
    flex-direction: column;
  }
`;

const CountrySwitcherTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
`;
